import {
	Box,
	createStyles,
	Group,
	Popover,
	Stack,
	TextInput,
} from '@mantine/core';
import { useInputState, useLocalStorage } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { trackEvent } from '@repo/common/analytics';
import { Button, IconButton } from '@repo/foundations';
import type { ExtensionsNames } from '@repo/secoda-editor';
import { modifyOpacityOfRgbaColor } from '@repo/theme/utils';
import { isEmpty } from 'lodash-es';
import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useCreateAIEmbedded } from '../../api';
import { useAiWorkspaceSettingEnabled } from '../../hooks/useAiWorkspaceSettingEnabled';
import { AIPromptSuggestions } from '../../pages/HomePage/components/AIPromptSuggestions';
import type { SecodaEditor } from '../Editor/SecodaEditor';
import { RichEditor } from '../RichEditor';
import SearchActions from '../Spotlight/components/SearchPalette/components/SearchActions';
import { AiPromptShotcuts } from './AiPromptShotcuts';
import { ANALYTICS_EVENTS, type PromptSuggestion } from './constants';

const DISABLED_EXTENSIONS: ExtensionsNames[] = [
	'emoji',
	'emojimenu',
	'query_block',
	'chart_block',
	'image',
	'embed',
	'holy',
	'page',
	'attachment',
	'comment',
	'blockmenu',
	'em',
	'code_block',
	'code_fence',
];

interface IStyleParams {
	popoverOpened: boolean;
}

type SearchMode = 'search' | 'ai';

const useStyles = createStyles((theme, { popoverOpened }: IStyleParams) => ({
	menuDropdown: {
		padding: 0,
		borderRadius: `0 0 ${theme.radius.md} ${theme.radius.md}`,
		boxShadow: theme.shadows.md,
		border: 0,
	},
	inputContainer: {
		width: '100%',
		borderRadius: popoverOpened
			? `${theme.radius.md} ${theme.radius.md} 0 0`
			: theme.radius.md,
		transitionTimingFunction: 'ease-in-out',
		transitionDuration: '100ms',
		boxShadow: `0px 0px 0px 1px ${theme.other.getColor('shadow/border')},
   0px 1px 4px 0px ${modifyOpacityOfRgbaColor(theme.other.getColor('shadow/button-shadow'), 0.08)},
   0px 4px 8px 0px ${modifyOpacityOfRgbaColor(theme.other.getColor('shadow/button-shadow'), 0.08)}`,
		border: 'none',
		backgroundColor: theme.other.getColor('surface/primary/default'),
	},
	icon: {
		color: theme.other.getColor('icon/primary/default'),
	},
	input: {
		border: 'none',
		lineHeight: '24px',
		padding: theme.spacing.md,
		height: 'auto',

		'&:focus': {
			outline: 'none',
			boxShadow: 'none',
		},
	},
}));

export function SearchAi() {
	const navigate = useNavigate();
	const enableAi = useAiWorkspaceSettingEnabled();
	const editorRef = useRef<SecodaEditor | null>(null);

	const inputRef = useRef<HTMLInputElement>(null);
	const [searchTerm, setSearchTerm] = useInputState('');
	const [shouldOpenPopover, setShouldOpenPopover] = useState(false);
	const [searchMode, setSearchMode] = useLocalStorage<'search' | 'ai'>({
		key: 'secoda-homepage-search-mode',
		defaultValue: enableAi ? 'ai' : 'search',
	});
	const aiEnabledAndSearchModeIsAi = useMemo(
		() => enableAi && searchMode === 'ai',
		[enableAi, searchMode]
	);

	const [selectedPromptSuggestion, setSelectedPromptSuggestion] =
		useState<PromptSuggestion | null>(null);

	const popoverOpened =
		shouldOpenPopover &&
		(searchMode === 'search' || !!selectedPromptSuggestion);

	const { classes } = useStyles({ popoverOpened });
	const { mutateAsync: createAIEmbedded, isPending: isCreatingAIEmbedded } =
		useCreateAIEmbedded();

	// Handle search term changes
	const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
		if (!shouldOpenPopover) {
			setShouldOpenPopover(true);
		}

		setSearchTerm(e.target.value);

		// Reset selected category if text is cleared
		if (e.target.value === '') {
			setSelectedPromptSuggestion(null);
		}
	};

	const handleNavigateToSearch = () => {
		let path = '/search';
		if (!isEmpty(searchTerm)) {
			path = `/search?term=${searchTerm}`;
		}
		navigate(path);
	};

	const handleSubmitAiPrompt = async () => {
		if (aiEnabledAndSearchModeIsAi && !isEmpty(searchTerm)) {
			try {
				if (isCreatingAIEmbedded) {
					return;
				}
				// Navigate to AI page with the prompt
				const prompt = await createAIEmbedded({
					data: {
						prompt: searchTerm,
					},
				});
				navigate(`/ai/${prompt.id}`);
			} catch {
				showNotification({
					title: 'Error',
					message: 'Failed to create AI prompt',
				});
			}
		}
	};

	// === Event handlers
	const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Escape') {
			setShouldOpenPopover(false);
		}

		if (e.key === 'Enter' && !e.shiftKey && !isEmpty(searchTerm)) {
			if (aiEnabledAndSearchModeIsAi) {
				handleSubmitAiPrompt();
			} else {
				// Navigate to search page with the term
				handleNavigateToSearch();
			}
		}
	};

	const handleSearchModeChange = (mode: SearchMode) => {
		setSearchTerm('');

		// Reset selected category when changing modes
		setSelectedPromptSuggestion(null);

		setSearchMode(mode);

		if (mode === 'search') {
			setShouldOpenPopover(true);
			inputRef.current?.focus();
		} else {
			setShouldOpenPopover(false);
			editorRef.current?.focus();
		}
	};

	const handleSelectStaticPromptSuggestion = useCallback(
		async (suggestion: PromptSuggestion) => {
			trackEvent(ANALYTICS_EVENTS.SEARCH_AI_PROMPT_CATEGORY_SUGGESTION_CLICK, {
				category: suggestion.label,
			});

			setSearchMode('ai');
			setSelectedPromptSuggestion(suggestion);
			setShouldOpenPopover(true);
			if (suggestion.inputPrefill) {
				setSearchTerm(suggestion.inputPrefill);
				editorRef.current?.clear();
				editorRef.current?.appendText(suggestion.inputPrefill);
			}
			editorRef.current?.focus();
		},
		[setSearchMode, setSearchTerm]
	);

	const handleSelectPromptSuggestion = useCallback(
		(suggestion: string) => {
			trackEvent(ANALYTICS_EVENTS.SEARCH_AI_PROMPT_CONCRETE_SUGGESTION_CLICK, {
				category: selectedPromptSuggestion?.label ?? '',
				suggestion: suggestion,
			});

			setSearchTerm(suggestion);
			editorRef.current?.clear();
			editorRef.current?.appendText(suggestion);
			setSelectedPromptSuggestion(null);
			editorRef.current?.focus();
			setShouldOpenPopover(false);
		},
		[setSearchTerm, setSelectedPromptSuggestion]
	);

	return (
		<Stack spacing="xl" align="center" w="100%">
			<Popover
				classNames={{ dropdown: classes.menuDropdown }}
				opened={popoverOpened}
				onClose={() => setShouldOpenPopover(false)}
				withinPortal
				closeOnClickOutside
				closeOnEscape
				offset={0}
				width="target"
			>
				<Popover.Target>
					<Stack className={classes.inputContainer} spacing={0}>
						{searchMode === 'ai' ? (
							<Box className={classes.input} onKeyDown={handleKeyDown}>
								<RichEditor
									dataTestId="ai-prompt-input"
									emptyDocPlaceholder="Ask about company data..."
									onChangeCallback={setSearchTerm}
									readOnly={isCreatingAIEmbedded}
									disableTopGap
									disableExtensions={DISABLED_EXTENSIONS}
									singleLineEditor
									disableResourceLinking
									disableChangeCallbackDebounce
									autoFocus
									editorRef={editorRef}
								/>
							</Box>
						) : (
							<TextInput
								ref={inputRef}
								classNames={{
									input: classes.input,
									icon: classes.icon,
								}}
								variant="unstyled"
								size="md"
								placeholder="Search company data..."
								value={searchTerm}
								onKeyDown={handleKeyDown}
								onChange={handleChange}
								data-testid="home-page-search-input"
								onFocusCapture={() => setShouldOpenPopover(true)}
								onBlurCapture={() => setShouldOpenPopover(false)}
								autoFocus
							/>
						)}
						<Box px="xs" pb="xs">
							<Group position="apart">
								<Group spacing="3xs">
									{enableAi && (
										<Button
											data-testid="home-page-search-ai-button"
											leftIconName="sparkles"
											onClick={() => handleSearchModeChange('ai')}
											variant="tertiary"
											data-active={searchMode === 'ai' ? 'true' : undefined}
										>
											AI
										</Button>
									)}
									<Button
										data-testid="home-page-search-search-button"
										leftIconName="search"
										onClick={() => handleSearchModeChange('search')}
										variant="tertiary"
										data-active={searchMode === 'search' ? 'true' : undefined}
									>
										Search
									</Button>
								</Group>
								<IconButton
									variant="primary"
									iconName="arrowUp"
									data-testid="submit-button"
									disabled={!searchTerm}
									size="md"
									onClick={
										aiEnabledAndSearchModeIsAi
											? handleSubmitAiPrompt
											: handleNavigateToSearch
									}
								/>
							</Group>
						</Box>
					</Stack>
				</Popover.Target>
				<Popover.Dropdown>
					{searchMode === 'search' && (
						<SearchActions
							searchTerm={searchTerm}
							showFilters={false}
							eventSource="home_page"
						/>
					)}
					{searchMode === 'ai' && selectedPromptSuggestion && (
						<AIPromptSuggestions
							searchTerm={searchTerm}
							suggestion={selectedPromptSuggestion}
							onSelect={handleSelectPromptSuggestion}
						/>
					)}
				</Popover.Dropdown>
			</Popover>

			{enableAi && searchMode === 'ai' && (
				<AiPromptShotcuts onSelect={handleSelectStaticPromptSuggestion} />
			)}
		</Stack>
	);
}
